export const AD_STUDIO_SCOPES = {
  CREATE: 'ad-studio:create',
  UPDATE: 'ad-studio:update',
  DELETE: 'ad-studio:delete',
  PUBLISH: 'ad-studio:publish',
  MEDIA_CREATE: 'media-library:create',
  MEDIA_DELETE: 'media-library:delete',
  MEDIA_UPDATE: 'media-library:update',
} as const

export type AdStudioScope = typeof AD_STUDIO_SCOPES[keyof typeof AD_STUDIO_SCOPES]

export const AI_CHAT_SCOPES = {
  POST: 'ai-chat:post',
} as const

export type AiChatScope = typeof AI_CHAT_SCOPES[keyof typeof AI_CHAT_SCOPES]

/**
 * What each scope used to be called, before Core moved to `service:action`.
 *
 * Both spellings have to be accepted while the rename rolls out: the frontend
 * and Core deploy separately, and a name we do not recognise reads as "not
 * granted" rather than as an error — so getting this wrong silently disables
 * buttons instead of failing loudly.
 *
 * Note `media_*` became `media-library:*`, a different prefix rather than the
 * same words re-punctuated. That is why these are spelled out one by one
 * instead of derived with a rule.
 *
 * **Delete this map once Core only emits the new names.** `hasScope` falls
 * through to a plain `includes` when a scope has no entry here, so removing
 * them is the whole cleanup.
 */
const LEGACY_SCOPE_NAMES: Record<string, string> = {
  [AD_STUDIO_SCOPES.CREATE]: 'ad_studio_create',
  [AD_STUDIO_SCOPES.UPDATE]: 'ad_studio_update',
  [AD_STUDIO_SCOPES.DELETE]: 'ad_studio_delete',
  [AD_STUDIO_SCOPES.PUBLISH]: 'ad_studio_publish',
  [AD_STUDIO_SCOPES.MEDIA_CREATE]: 'media_create',
  [AD_STUDIO_SCOPES.MEDIA_DELETE]: 'media_delete',
  [AD_STUDIO_SCOPES.MEDIA_UPDATE]: 'media_update',
}

/** Whether a token grants a scope, under either its current or its former name. */
export const hasScope = (granted: readonly string[], scope: string): boolean => {
  if (granted.includes(scope)) {
    return true
  }
  const legacy = LEGACY_SCOPE_NAMES[scope]
  return !!legacy && granted.includes(legacy)
}
